import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  addAdminCategoryBanner,
  createAdminCategory,
  getAdminCategories,
  moveAdminCategory,
  removeAdminCategoryBanner,
  updateAdminCategory,
  uploadAdminCategoryBanner,
  type AdminCategoryTreeNode,
} from "@/lib/admin-api-client";
import { flattenCategoryTree } from "@/components/admin/adminCategoryTree";
import { describeAdminError } from "@/components/admin/adminErrors";

const INPUT_CLASS = "w-full rounded-sm border border-line px-3 py-2 text-sm text-ink";
const NODE_INPUT_CLASS = "w-48 rounded-sm border border-line px-2 py-1 text-sm text-ink";

/** Entier positif optionnel (âge minimum) — même logique que `parseNonNegativeInt()` de `AdminCatalogueProduitDetailPage.tsx`. */
function parseNonNegativeInt(raw: string): number | null {
  if (!raw) return null;
  const value = Number.parseInt(raw, 10);
  if (!Number.isInteger(value) || value < 0 || String(value) !== raw) return null;
  return value;
}

/**
 * `/admin/catalogue/categories` — équivalent de
 * apps/web/app/(admin)/admin/catalogue/categories/page.tsx : arbre complet +
 * création/édition inline (toujours des `<details>`, mais les formulaires
 * natifs `action={serverAction}` deviennent des `onSubmit` qui lisent le
 * `FormData` du formulaire et appellent `useMutation` — même forme de champs,
 * mêmes noms `name=`, pour rester un port fidèle), plus (ce lot) bannière par
 * catégorie et réordonnancement simple entre frères/sœurs. Pas de suppression
 * ni de réaffectation de parent à ce lot (inchangé de l'original, cf.
 * commentaire de classe de `AdminCategoriesService`).
 *
 * Écart assumé (cf. rapport) : le pattern `?error=`/`?success=` de
 * l'original (redirection serveur) devient un état de mutation local
 * affiché au même endroit — plus simple en contexte SPA rendu client, pas de
 * navigation complète requise pour rafraîchir l'arbre (`invalidateQueries`
 * s'en charge).
 */
export function AdminCatalogueCategoriesPage() {
  const queryClient = useQueryClient();
  const categoriesQuery = useQuery({
    queryKey: ["admin", "categories"],
    queryFn: () => getAdminCategories(),
  });

  const [feedback, setFeedback] = useState<{ type: "error" | "success"; message: string } | null>(null);

  const invalidateCategories = () => queryClient.invalidateQueries({ queryKey: ["admin", "categories"] });

  const createMutation = useMutation({
    mutationFn: (input: Parameters<typeof createAdminCategory>[1]) => createAdminCategory(undefined, input),
    onSuccess: () => {
      setFeedback({ type: "success", message: "Catégorie enregistrée." });
      invalidateCategories();
    },
    onError: (err) => setFeedback({ type: "error", message: describeAdminError(err) }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: Parameters<typeof updateAdminCategory>[2] }) =>
      updateAdminCategory(undefined, id, input),
    onSuccess: () => {
      setFeedback({ type: "success", message: "Catégorie enregistrée." });
      invalidateCategories();
    },
    onError: (err) => setFeedback({ type: "error", message: describeAdminError(err) }),
  });

  const addBannerMutation = useMutation({
    mutationFn: ({ id, url }: { id: string; url: string }) => addAdminCategoryBanner(undefined, id, url),
    onSuccess: () => {
      setFeedback({ type: "success", message: "Bannière ajoutée." });
      invalidateCategories();
    },
    onError: (err) => setFeedback({ type: "error", message: describeAdminError(err) }),
  });

  const uploadBannerMutation = useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) => uploadAdminCategoryBanner(undefined, id, file),
    onSuccess: () => {
      setFeedback({ type: "success", message: "Bannière envoyée." });
      invalidateCategories();
    },
    onError: (err) => setFeedback({ type: "error", message: describeAdminError(err) }),
  });

  const removeBannerMutation = useMutation({
    mutationFn: (id: string) => removeAdminCategoryBanner(undefined, id),
    onSuccess: () => {
      setFeedback({ type: "success", message: "Bannière supprimée." });
      invalidateCategories();
    },
    onError: (err) => setFeedback({ type: "error", message: describeAdminError(err) }),
  });

  const moveMutation = useMutation({
    mutationFn: ({ id, direction }: { id: string; direction: "up" | "down" }) =>
      moveAdminCategory(undefined, id, direction),
    onSuccess: () => {
      invalidateCategories();
    },
    onError: (err) => setFeedback({ type: "error", message: describeAdminError(err) }),
  });

  function handleCreateSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFeedback(null);
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);
    const name = String(formData.get("name") ?? "").trim();
    if (!name) {
      setFeedback({ type: "error", message: "Le nom est obligatoire." });
      return;
    }
    const parentId = String(formData.get("parentId") ?? "").trim();
    const slug = String(formData.get("slug") ?? "").trim();
    const descriptionShort = String(formData.get("descriptionShort") ?? "").trim();
    const descriptionSeo = String(formData.get("descriptionSeo") ?? "").trim();
    const ageRestricted = formData.get("ageRestricted") === "on";
    const minAgeRaw = String(formData.get("minAge") ?? "").trim();
    let minAge: number | undefined;
    if (minAgeRaw) {
      const parsed = parseNonNegativeInt(minAgeRaw);
      if (parsed === null) {
        setFeedback({ type: "error", message: "L'âge minimum doit être un entier positif." });
        return;
      }
      minAge = parsed;
    }

    createMutation.mutate(
      {
        name,
        parentId: parentId || undefined,
        slug: slug || undefined,
        descriptionShort: descriptionShort || undefined,
        descriptionSeo: descriptionSeo || undefined,
        ageRestricted,
        minAge,
      },
      { onSuccess: () => formEl.reset() },
    );
  }

  function handleUpdateSubmit(event: FormEvent<HTMLFormElement>, id: string) {
    event.preventDefault();
    setFeedback(null);
    const formData = new FormData(event.currentTarget);
    const minAgeRaw = String(formData.get("minAge") ?? "").trim();
    let minAge: number | undefined;
    if (minAgeRaw) {
      const parsed = parseNonNegativeInt(minAgeRaw);
      if (parsed === null) {
        setFeedback({ type: "error", message: "L'âge minimum doit être un entier positif." });
        return;
      }
      minAge = parsed;
    }

    updateMutation.mutate({
      id,
      input: {
        name: String(formData.get("name") ?? "").trim() || undefined,
        isVisible: formData.get("isVisible") === "on",
        descriptionShort: String(formData.get("descriptionShort") ?? "").trim() || undefined,
        descriptionSeo: String(formData.get("descriptionSeo") ?? "").trim() || undefined,
        // Case à cocher : toujours envoyée (jamais `undefined`), même convention que
        // `isVisible` ci-dessus — sinon décocher la case n'aurait jamais d'effet côté API.
        ageRestricted: formData.get("ageRestricted") === "on",
        minAge,
      },
    });
  }

  function handleAddBannerUrlSubmit(event: FormEvent<HTMLFormElement>, id: string) {
    event.preventDefault();
    setFeedback(null);
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);
    const url = String(formData.get("bannerUrl") ?? "").trim();
    if (!url) {
      setFeedback({ type: "error", message: "L'URL de la bannière est obligatoire." });
      return;
    }
    addBannerMutation.mutate({ id, url }, { onSuccess: () => formEl.reset() });
  }

  function handleUploadBannerSubmit(event: FormEvent<HTMLFormElement>, id: string) {
    event.preventDefault();
    setFeedback(null);
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);
    const file = formData.get("bannerFile");
    if (!(file instanceof File) || file.size === 0) {
      setFeedback({ type: "error", message: "Choisissez un fichier image à envoyer." });
      return;
    }
    uploadBannerMutation.mutate({ id, file }, { onSuccess: () => formEl.reset() });
  }

  function handleRemoveBanner(id: string) {
    setFeedback(null);
    removeBannerMutation.mutate(id);
  }

  function handleMove(id: string, direction: "up" | "down") {
    setFeedback(null);
    moveMutation.mutate({ id, direction });
  }

  const categories = categoriesQuery.data ?? [];
  const flatCategories = flattenCategoryTree(categories);
  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div>
      <div className="flex items-center gap-3">
        <Link to="/admin/catalogue" className="text-sm font-medium text-navy hover:underline">
          ← Catalogue
        </Link>
      </div>

      <h1 className="mt-2 font-display text-2xl font-semibold text-ink">Catégories</h1>
      <p className="mt-1 text-sm text-muted">
        {flatCategories.length} catégorie(s). Pas de suppression ni de réaffectation de parent à ce lot — le
        réordonnancement ↑/↓ ci-dessous ne fait qu'échanger la position avec le frère/sœur adjacent.
      </p>

      {categoriesQuery.isLoading && <p className="mt-4 text-sm text-muted">Chargement…</p>}
      {categoriesQuery.isError && (
        <p className="mt-4 rounded-sm border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">
          {describeAdminError(categoriesQuery.error)}
        </p>
      )}

      {feedback && (
        <p
          className={[
            "mt-4 rounded-sm border px-4 py-3 text-sm",
            feedback.type === "error"
              ? "border-red-300 bg-red-50 text-red-700"
              : "border-green-300 bg-green-50 text-green-700",
          ].join(" ")}
        >
          {feedback.message}
        </p>
      )}

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[2fr_1fr]">
        <div className="rounded-sm border border-line bg-white p-4">
          <h2 className="font-display text-lg font-semibold text-ink">Arbre</h2>
          {categories.length === 0 ? (
            <p className="mt-3 text-sm text-muted">Aucune catégorie pour le moment.</p>
          ) : (
            <ul className="mt-3 space-y-1">
              {categories.map((node, index) => (
                <CategoryNode
                  key={node.id}
                  node={node}
                  index={index}
                  siblingCount={categories.length}
                  onSubmit={handleUpdateSubmit}
                  disabled={isPending}
                  onAddBannerUrl={handleAddBannerUrlSubmit}
                  onUploadBanner={handleUploadBannerSubmit}
                  onRemoveBanner={handleRemoveBanner}
                  onMove={handleMove}
                  addBannerPending={addBannerMutation.isPending}
                  uploadBannerPending={uploadBannerMutation.isPending}
                  removeBannerPending={removeBannerMutation.isPending}
                  movePending={moveMutation.isPending}
                />
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-sm border border-line bg-white p-4">
          <h2 className="font-display text-lg font-semibold text-ink">Nouvelle catégorie</h2>
          <form onSubmit={handleCreateSubmit} className="mt-3 space-y-4">
            <div className="flex flex-col gap-1">
              <label htmlFor="name" className="text-sm font-medium text-ink">
                Nom
              </label>
              <input id="name" name="name" type="text" required className={INPUT_CLASS} />
            </div>

            <div className="flex flex-col gap-1">
              <label htmlFor="parentId" className="text-sm font-medium text-ink">
                Catégorie parente
              </label>
              <select id="parentId" name="parentId" defaultValue="" className={INPUT_CLASS}>
                <option value="">— Racine —</option>
                {flatCategories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {"— ".repeat(cat.level)}
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <label htmlFor="slug" className="text-sm font-medium text-ink">
                Slug (optionnel)
              </label>
              <input id="slug" name="slug" type="text" className={INPUT_CLASS} />
            </div>

            <div className="flex flex-col gap-1">
              <label htmlFor="descriptionShort" className="text-sm font-medium text-ink">
                Description courte (optionnel)
              </label>
              <textarea id="descriptionShort" name="descriptionShort" rows={3} className={INPUT_CLASS} />
            </div>

            <div className="flex flex-col gap-1">
              <label htmlFor="descriptionSeo" className="text-sm font-medium text-ink">
                Description SEO (optionnel)
              </label>
              <textarea id="descriptionSeo" name="descriptionSeo" rows={3} className={INPUT_CLASS} />
            </div>

            <label className="flex items-center gap-2 text-sm text-ink">
              <input type="checkbox" name="ageRestricted" />
              Rayon à âge restreint (vente interdite aux mineurs)
            </label>

            <div className="flex flex-col gap-1">
              <label htmlFor="minAge" className="text-sm font-medium text-ink">
                Âge minimum (optionnel — 18 par défaut si la case ci-dessus est cochée)
              </label>
              <input id="minAge" name="minAge" type="number" min={0} step={1} className={INPUT_CLASS} />
            </div>

            <button
              type="submit"
              disabled={isPending}
              className="rounded-sm bg-navy px-4 py-2 text-sm font-medium text-cream hover:bg-navy/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Créer
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function CategoryNode({
  node,
  index,
  siblingCount,
  onSubmit,
  disabled,
  onAddBannerUrl,
  onUploadBanner,
  onRemoveBanner,
  onMove,
  addBannerPending,
  uploadBannerPending,
  removeBannerPending,
  movePending,
}: {
  node: AdminCategoryTreeNode;
  index: number;
  siblingCount: number;
  onSubmit: (event: FormEvent<HTMLFormElement>, id: string) => void;
  disabled: boolean;
  onAddBannerUrl: (event: FormEvent<HTMLFormElement>, id: string) => void;
  onUploadBanner: (event: FormEvent<HTMLFormElement>, id: string) => void;
  onRemoveBanner: (id: string) => void;
  onMove: (id: string, direction: "up" | "down") => void;
  addBannerPending: boolean;
  uploadBannerPending: boolean;
  removeBannerPending: boolean;
  movePending: boolean;
}) {
  const isFirst = index === 0;
  const isLast = index === siblingCount - 1;

  return (
    <li>
      <details className="rounded-sm border border-line">
        <summary className="flex cursor-pointer items-center justify-between gap-2 px-3 py-2 text-sm text-ink">
          <span className="flex items-center gap-2">
            <span className="font-medium">{node.name}</span>
            <span className="text-xs text-muted">/{node.slug}</span>
            {!node.isVisible && (
              <span className="rounded-sm border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[11px] text-amber-700">
                Masquée
              </span>
            )}
            {node.ageRestricted && (
              <span className="rounded-sm border border-red-300 bg-red-50 px-1.5 py-0.5 text-[11px] text-red-700">
                Âge restreint{node.minAge != null ? ` (${node.minAge}+)` : ""}
              </span>
            )}
          </span>

          <span className="flex items-center gap-1">
            <button
              type="button"
              onClick={(event) => {
                event.preventDefault();
                onMove(node.id, "up");
              }}
              disabled={isFirst || movePending}
              title="Monter"
              aria-label="Monter parmi les frères et sœurs"
              className="flex h-6 w-6 items-center justify-center rounded-sm border border-line text-xs text-ink hover:bg-cream disabled:cursor-not-allowed disabled:opacity-40"
            >
              ↑
            </button>
            <button
              type="button"
              onClick={(event) => {
                event.preventDefault();
                onMove(node.id, "down");
              }}
              disabled={isLast || movePending}
              title="Descendre"
              aria-label="Descendre parmi les frères et sœurs"
              className="flex h-6 w-6 items-center justify-center rounded-sm border border-line text-xs text-ink hover:bg-cream disabled:cursor-not-allowed disabled:opacity-40"
            >
              ↓
            </button>
          </span>
        </summary>

        <div className="border-t border-line px-3 py-3">
          <form onSubmit={(event) => onSubmit(event, node.id)} className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1">
              <label htmlFor={`name-${node.id}`} className="text-xs font-medium text-muted">
                Nom
              </label>
              <input
                id={`name-${node.id}`}
                name="name"
                type="text"
                defaultValue={node.name}
                className={NODE_INPUT_CLASS}
              />
            </div>

            <div className="flex flex-col gap-1">
              <label htmlFor={`descriptionShort-${node.id}`} className="text-xs font-medium text-muted">
                Description courte
              </label>
              <input
                id={`descriptionShort-${node.id}`}
                name="descriptionShort"
                type="text"
                defaultValue={node.descriptionShort ?? ""}
                className="w-64 rounded-sm border border-line px-2 py-1 text-sm text-ink"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label htmlFor={`descriptionSeo-${node.id}`} className="text-xs font-medium text-muted">
                Description SEO
              </label>
              <input
                id={`descriptionSeo-${node.id}`}
                name="descriptionSeo"
                type="text"
                defaultValue={node.descriptionSeo ?? ""}
                className="w-64 rounded-sm border border-line px-2 py-1 text-sm text-ink"
              />
            </div>

            <label className="flex items-center gap-2 pb-2 text-sm text-ink">
              <input type="checkbox" name="isVisible" defaultChecked={node.isVisible} />
              Visible côté boutique
            </label>

            <label className="flex items-center gap-2 pb-2 text-sm text-ink">
              <input type="checkbox" name="ageRestricted" defaultChecked={node.ageRestricted} />
              Âge restreint
            </label>

            <div className="flex flex-col gap-1">
              <label htmlFor={`minAge-${node.id}`} className="text-xs font-medium text-muted">
                Âge minimum
              </label>
              <input
                id={`minAge-${node.id}`}
                name="minAge"
                type="number"
                min={0}
                step={1}
                defaultValue={node.minAge ?? ""}
                className="w-24 rounded-sm border border-line px-2 py-1 text-sm text-ink"
              />
            </div>

            <button
              type="submit"
              disabled={disabled}
              className="rounded-sm border border-navy px-3 py-1 text-xs font-medium text-navy hover:bg-navy hover:text-cream disabled:cursor-not-allowed disabled:opacity-50"
            >
              Enregistrer
            </button>
          </form>

          <div className="mt-4 border-t border-line pt-3">
            <p className="text-xs font-medium text-muted">
              Bannière ("Acheter par rayon" côté vitrine) — pas de réordonnancement par glisser-déposer à ce lot.
            </p>

            {node.bannerMedia ? (
              <div className="relative mt-2 w-40 overflow-hidden rounded-sm border border-line">
                <img
                  src={node.bannerMedia.url}
                  alt={`Bannière de ${node.name}`}
                  className="aspect-video w-full object-cover"
                />
                <button
                  type="button"
                  onClick={() => onRemoveBanner(node.id)}
                  disabled={removeBannerPending}
                  title="Supprimer la bannière"
                  aria-label="Supprimer la bannière"
                  className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-white/90 text-xs font-bold text-red-700 hover:bg-white disabled:cursor-not-allowed disabled:opacity-50"
                >
                  ×
                </button>
              </div>
            ) : (
              <p className="mt-2 text-sm text-muted">Aucune bannière pour cette catégorie.</p>
            )}

            <form
              onSubmit={(event) => onAddBannerUrl(event, node.id)}
              className="mt-3 flex flex-wrap items-end gap-2"
            >
              <div className="flex flex-1 flex-col gap-1">
                <label htmlFor={`bannerUrl-${node.id}`} className="text-xs font-medium text-muted">
                  Ajouter/remplacer par une image (URL)
                </label>
                <input
                  id={`bannerUrl-${node.id}`}
                  name="bannerUrl"
                  type="text"
                  placeholder="https://…"
                  className="w-64 rounded-sm border border-line px-2 py-1 text-sm text-ink"
                />
              </div>
              <button
                type="submit"
                disabled={addBannerPending}
                className="rounded-sm border border-navy px-3 py-1 text-xs font-medium text-navy hover:bg-navy hover:text-cream disabled:cursor-not-allowed disabled:opacity-50"
              >
                {addBannerPending ? "Ajout…" : "Ajouter"}
              </button>
            </form>

            <form
              onSubmit={(event) => onUploadBanner(event, node.id)}
              className="mt-2 flex flex-wrap items-end gap-2"
            >
              <div className="flex flex-1 flex-col gap-1">
                <label htmlFor={`bannerFile-${node.id}`} className="text-xs font-medium text-muted">
                  Ou envoyer un fichier image (2 Mo max)
                </label>
                <input
                  id={`bannerFile-${node.id}`}
                  name="bannerFile"
                  type="file"
                  accept="image/*"
                  className="w-64 rounded-sm border border-line px-2 py-1 text-sm text-ink"
                />
              </div>
              <button
                type="submit"
                disabled={uploadBannerPending}
                className="rounded-sm border border-navy px-3 py-1 text-xs font-medium text-navy hover:bg-navy hover:text-cream disabled:cursor-not-allowed disabled:opacity-50"
              >
                {uploadBannerPending ? "Envoi…" : "Envoyer"}
              </button>
            </form>
          </div>
        </div>
      </details>

      {node.children.length > 0 && (
        <ul className="mt-1 space-y-1 pl-6">
          {node.children.map((child, childIndex) => (
            <CategoryNode
              key={child.id}
              node={child}
              index={childIndex}
              siblingCount={node.children.length}
              onSubmit={onSubmit}
              disabled={disabled}
              onAddBannerUrl={onAddBannerUrl}
              onUploadBanner={onUploadBanner}
              onRemoveBanner={onRemoveBanner}
              onMove={onMove}
              addBannerPending={addBannerPending}
              uploadBannerPending={uploadBannerPending}
              removeBannerPending={removeBannerPending}
              movePending={movePending}
            />
          ))}
        </ul>
      )}
    </li>
  );
}
